import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, RefreshCw, TrendingUp, ShieldAlert, ScrollText, Settings2, PhoneCall } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  adminListDisputes,
  adminReliabilityStats,
  adminO2OSearch,
  adminAuditTimeline,
} from '../../api/adminO2O';
import DisputeDetailPanel from './DisputeDetailPanel';
import { AdminCallSettingsTab, AdminCallStatsTab } from './AdminO2OCallsPanel';

const fmt = (iso, locale = 'it-IT') => (iso ? new Date(iso).toLocaleString(locale) : '—');

const StateBadge = ({ state }) => {
  const map = {
    OPEN: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
    REVIEWING: 'bg-blue-500/15 text-blue-200 border-blue-500/30',
    ACCEPTED: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
    REJECTED: 'bg-red-500/15 text-red-200 border-red-500/30',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${map[state] || 'bg-neutral-500/15 text-neutral-200'}`}>
      {state}
    </span>
  );
};

const DisputesTab = ({ selectedId, onSelect }) => {
  const { t, i18n } = useTranslation('admin');
  const locale = i18n.language === 'en' ? 'en-GB' : 'it-IT';
  const [state, setState] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    adminListDisputes({ state: state || null, limit: 100 })
      .then(setData)
      .catch(() => setData({ items: [] }))
      .finally(() => setLoading(false));
  }, [state, refreshTick]);

  const filters = ['', 'OPEN', 'REVIEWING', 'ACCEPTED', 'REJECTED'];
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[420px_1fr]">
      <div className="rounded-lg border border-border">
        <div className="flex items-center justify-between border-b border-border p-3">
          <div className="flex gap-1">
            {filters.map((f) => (
              <Button
                key={f || 'all'}
                size="sm"
                variant={state === f ? 'default' : 'outline'}
                onClick={() => setState(f)}
                className="h-7 px-2 text-[10px]"
                data-testid={`disputes-filter-${f || 'all'}`}
              >
                {t(`disputes.filters.${f || 'all'}`, { defaultValue: f || t('disputes.filters.all') })}
              </Button>
            ))}
          </div>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => setRefreshTick((tick) => tick + 1)}
            data-testid="disputes-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="max-h-[70vh] overflow-auto p-2">
          {loading && (
            <div className="p-6 text-center text-muted-foreground">
              <Loader2 className="mx-auto h-5 w-5 animate-spin" />
            </div>
          )}
          {!loading && data?.items?.length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('disputes.empty')}</p>
          )}
          <ul className="space-y-1">
            {(data?.items || []).map((d) => (
              <li key={d.id}>
                <button
                  className={`w-full rounded-md p-2 text-left text-xs transition ${
                    selectedId === d.id ? 'bg-amber-500/10 ring-1 ring-amber-500/40' : 'hover:bg-white/5'
                  }`}
                  onClick={() => onSelect(d.id)}
                  data-testid={`dispute-row-${d.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] truncate">{d.session_id}</span>
                    <StateBadge state={d.state} />
                  </div>
                  <p className="mt-1 truncate text-[11px]">{d.motivo}</p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">{fmt(d.created_at, locale)}</p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="rounded-lg border border-border">
        {!selectedId && (
          <div className="flex h-64 flex-col items-center justify-center gap-2 text-muted-foreground">
            <ShieldAlert className="h-8 w-8" />
            <p className="text-sm">{t('disputes.select_hint')}</p>
          </div>
        )}
        {selectedId && (
          <DisputeDetailPanel
            disputeId={selectedId}
            onClose={() => onSelect(null)}
            onChanged={() => setRefreshTick((tick) => tick + 1)}
          />
        )}
      </div>
    </div>
  );
};

const SearchTab = ({ onOpenDispute }) => {
  const { t, i18n } = useTranslation('admin');
  const locale = i18n.language === 'en' ? 'en-GB' : 'it-IT';
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const res = await adminO2OSearch(q.trim());
      setData(res);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder={t('search.placeholder')}
          className="flex-1"
          data-testid="admin-o2o-search-input"
        />
        <Button onClick={submit} disabled={loading || !q.trim()} className="gold-gradient" data-testid="admin-o2o-search-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>
      {data && (
        <div className="space-y-4">
          {data.disputes.length > 0 && (
            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">{t('search.sections.disputes')}</p>
              <ul className="space-y-1">
                {data.disputes.map((d) => (
                  <li key={d.id}>
                    <button
                      className="w-full rounded-md p-2 text-left text-xs hover:bg-white/5"
                      onClick={() => onOpenDispute(d.id)}
                      data-testid={`search-dispute-${d.id}`}
                    >
                      <div className="flex justify-between">
                        <span className="font-mono">{d.session_id}</span>
                        <StateBadge state={d.state} />
                      </div>
                      <p className="text-muted-foreground">{d.motivo}</p>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.sessions.length > 0 && (
            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">{t('search.sections.sessions')}</p>
              <ul className="space-y-1 text-xs">
                {data.sessions.map((s) => (
                  <li key={s.id} className="rounded-md p-2 hover:bg-white/5" data-testid={`search-session-${s.session_id}`}>
                    <div className="flex justify-between">
                      <span className="font-mono">{s.session_id}</span>
                      <span className="text-muted-foreground">{s.state}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      {(s.price_cents / 100).toFixed(2)} € · {fmt(s.slot_start_utc, locale)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.users.length > 0 && (
            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">{t('search.sections.users')}</p>
              <ul className="space-y-1 text-xs">
                {data.users.map((u) => (
                  <li key={u.id} className="flex items-center gap-2">
                    {u.avatar_url && <img alt="" src={u.avatar_url} className="h-6 w-6 shrink-0 rounded-full object-cover object-center" />}
                    <span className="font-medium">{u.username}</span>
                    <span className="text-[10px] text-muted-foreground">{u.is_creator ? 'creator' : 'user'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.disputes.length === 0 && data.sessions.length === 0 && data.users.length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground" data-testid="admin-o2o-search-empty">{t('search.empty')}</p>
          )}
        </div>
      )}
    </div>
  );
};

const ReliabilityTab = () => {
  const { t } = useTranslation('admin');
  const [scope, setScope] = useState('creator');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    adminReliabilityStats({ scope, limit: 50 })
      .then(setData)
      .catch(() => setData({ items: [] }))
      .finally(() => setLoading(false));
  }, [scope]);
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button size="sm" variant={scope === 'creator' ? 'default' : 'outline'} onClick={() => setScope('creator')} data-testid="reliability-scope-creator">{t('reliability.creator_scope')}</Button>
        <Button size="sm" variant={scope === 'user' ? 'default' : 'outline'} onClick={() => setScope('user')} data-testid="reliability-scope-user">{t('reliability.user_scope')}</Button>
      </div>
      {loading ? (
        <div className="p-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="overflow-auto rounded-lg border border-border">
          <table className="w-full text-xs" data-testid="reliability-table">
            <thead className="bg-black/20">
              <tr className="text-left">
                <th className="p-2">{t('reliability.columns.user')}</th>
                <th className="p-2 text-right">{t('reliability.columns.sessions')}</th>
                <th className="p-2 text-right">{t('reliability.columns.completed')}</th>
                <th className="p-2 text-right">{t('reliability.columns.completion')}</th>
                <th className="p-2 text-right">{t('reliability.columns.no_show_own')}</th>
                <th className="p-2 text-right">{t('reliability.columns.disputes')}</th>
                <th className="p-2 text-right">{t('reliability.columns.accepted')}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((row) => (
                <tr key={row.user_id} className="border-t border-border" data-testid={`reliability-row-${row.user_id}`}>
                  <td className="p-2">{row.user?.username || row.user_id?.slice(0, 8)}</td>
                  <td className="p-2 text-right">{row.total_sessions}</td>
                  <td className="p-2 text-right">{row.completed}</td>
                  <td className="p-2 text-right">{(row.completion_rate * 100).toFixed(0)}%</td>
                  <td className="p-2 text-right">
                    {row.no_show_own}{' '}
                    <span className="text-[10px] text-muted-foreground">({(row.no_show_rate_own * 100).toFixed(0)}%)</span>
                  </td>
                  <td className="p-2 text-right">{row.dispute_opened}</td>
                  <td className="p-2 text-right">{row.dispute_accepted}</td>
                </tr>
              ))}
              {(!data?.items || data.items.length === 0) && (
                <tr><td colSpan="7" className="p-4 text-center text-muted-foreground">{t('reliability.empty')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AuditTab = () => {
  const { t, i18n } = useTranslation('admin');
  const locale = i18n.language === 'en' ? 'en-GB' : 'it-IT';
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const res = await adminAuditTimeline(q.trim());
      setData(res);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder={t('audit.placeholder')}
          className="flex-1"
          data-testid="audit-search-input"
        />
        <Button onClick={submit} disabled={loading || !q.trim()} className="gold-gradient" data-testid="audit-search-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScrollText className="h-4 w-4" />}
        </Button>
      </div>
      {data && (
        <div className="rounded-lg border border-border">
          <div className="border-b border-border p-3">
            <p className="text-xs text-muted-foreground">{t('audit.count_line', { count: data.count, session_id: data.session_id })}</p>
          </div>
          <ul className="max-h-[60vh] overflow-auto p-2 text-[11px]">
            {data.events.map((e) => (
              <li key={e.id} className="mb-1 border-b border-border/50 p-2 last:border-b-0">
                <div className="flex justify-between">
                  <span className="font-mono uppercase">{e.event_type}</span>
                  <span className="text-[10px] text-muted-foreground">{fmt(e.created_at, locale)}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">actor: {e.actor_type} {e.actor_id && `(${e.actor_id.slice(0, 8)})`}</p>
                {e.metadata && Object.keys(e.metadata).length > 0 && (
                  <pre className="mt-1 max-h-24 overflow-auto rounded bg-black/30 p-1 text-[10px]">{JSON.stringify(e.metadata, null, 2)}</pre>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const AdminO2ODashboard = () => {
  const { t } = useTranslation('admin');
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [tab, setTab] = useState('disputes');
  const openDispute = (id) => {
    setSelectedDispute(id);
    setTab('disputes');
  };
  return (
    <div className="mx-auto max-w-7xl px-4 py-6" data-testid="admin-o2o-dashboard">
      <div className="mb-4">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('dashboard.eyebrow')}</p>
        <h1 className="text-2xl font-semibold">{t('dashboard.title')}</h1>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="disputes" data-testid="admin-o2o-tab-disputes">
            <ShieldAlert className="mr-1.5 h-3.5 w-3.5" /> {t('tabs.disputes')}
          </TabsTrigger>
          <TabsTrigger value="search" data-testid="admin-o2o-tab-search">
            <Search className="mr-1.5 h-3.5 w-3.5" /> {t('tabs.search')}
          </TabsTrigger>
          <TabsTrigger value="reliability" data-testid="admin-o2o-tab-reliability">
            <TrendingUp className="mr-1.5 h-3.5 w-3.5" /> {t('tabs.reliability')}
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-o2o-tab-audit">
            <ScrollText className="mr-1.5 h-3.5 w-3.5" /> {t('tabs.audit')}
          </TabsTrigger>
          <TabsTrigger value="call-settings" data-testid="admin-o2o-tab-call-settings">
            <Settings2 className="mr-1.5 h-3.5 w-3.5" /> Chiamate · Impostazioni
          </TabsTrigger>
          <TabsTrigger value="call-stats" data-testid="admin-o2o-tab-call-stats">
            <PhoneCall className="mr-1.5 h-3.5 w-3.5" /> Chiamate · Statistiche
          </TabsTrigger>
        </TabsList>
        <TabsContent value="disputes" className="mt-4">
          <DisputesTab selectedId={selectedDispute} onSelect={setSelectedDispute} />
        </TabsContent>
        <TabsContent value="search" className="mt-4">
          <SearchTab onOpenDispute={openDispute} />
        </TabsContent>
        <TabsContent value="reliability" className="mt-4">
          <ReliabilityTab />
        </TabsContent>
        <TabsContent value="audit" className="mt-4">
          <AuditTab />
        </TabsContent>
        <TabsContent value="call-settings" className="mt-4">
          <AdminCallSettingsTab />
        </TabsContent>
        <TabsContent value="call-stats" className="mt-4">
          <AdminCallStatsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminO2ODashboard;
